import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Form from '@nineeng/forms';
import { useState } from 'react';
import { Tabs } from '@repo/components';
import { Site } from '@repo/shared/domain';
import { EventEmitter, guardedClient as guardedRequest } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { getSite, updateSite } from 'src/api/admin/sites';

// `gracePeriodCapEnabled` is a UI-only flag (not persisted): the cap is "on" whenever a value is set,
// mirroring the 2.0 admin UI. It gates the two cap inputs and is stripped before saving.
type SiteFormValues = Site & { gracePeriodCapEnabled?: boolean };

// Minimal timezones list
const minimalTimezones = [
	{ tzCode: 'Europe/Brussels', label: 'Brussels (CET/CEST)' },
	{ tzCode: 'Europe/London', label: 'London (GMT/BST)' },
	{ tzCode: 'Europe/Paris', label: 'Paris (CET/CEST)' },
	{ tzCode: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
	{ tzCode: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
	{ tzCode: 'America/New_York', label: 'New York (EST/EDT)' },
	{ tzCode: 'America/Chicago', label: 'Chicago (CST/CDT)' },
	{ tzCode: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
	{ tzCode: 'Asia/Tokyo', label: 'Tokyo (JST)' },
	{ tzCode: 'Asia/Shanghai', label: 'Shanghai (CST)' },
	{ tzCode: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
	{ tzCode: 'UTC', label: 'UTC' },
];

export const Route = createFileRoute('/_admin/system/sites/$id')({
	loader: async ({ params }) => {
		const site = await getSite(params.id);
		return { site };
	},
	component: SiteDetailPage,
});

function GeneralTab({ data, onChange }: { data: SiteFormValues; onChange: (data: SiteFormValues) => void }) {
	// SettingsSection ignores `prerequisites`, so the cap inputs are shown/hidden by conditionally
	// including them in the schema; toggling the checkbox re-renders this tab via onChange.
	const showCap = !!data.gracePeriodCapEnabled;
	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Name',
						rowDescription: 'This is the name users will see in the mails and notifications they receive.',
						name: 'name',
						type: 'TextField',
						required: true,
					},
					{ rowLabel: 'Description', name: 'description', type: 'TextField' },
					{ rowLabel: 'Address Line', name: 'address.address', type: 'TextField' },
					{ rowLabel: 'City', name: 'address.city', type: 'TextField' },
					{ rowLabel: 'Postal/ZIP code', name: 'address.postalCode', type: 'TextField' },
					{ rowLabel: 'Country', name: 'address.country', type: 'TextField' },
					{
						rowLabel: 'Timezone',
						name: 'timezone',
						type: 'AutoCompleteByID',
						options: minimalTimezones.map((t) => ({ ...t, id: t.tzCode })),
						getOptionLabel: (o: { label: string }) => o.label,
						required: true,
					},
					{
						rowLabel: 'Enable Grace Period Cap',
						rowDescription:
							"Cap the grace period any event type can use at this site. When off, event types' grace periods are not capped here.",
						name: 'gracePeriodCapEnabled',
						type: 'Checkbox',
					},
					...(showCap
						? [
								{
									rowLabel: 'Grace Period Cap Before (minutes)',
									rowDescription:
										"Maximum grace period before an event allowed at this site. Caps each event type's value.",
									name: 'allowMinutesPre',
									type: 'TextField',
									htmlType: 'number',
								},
								{
									rowLabel: 'Grace Period Cap After (minutes)',
									rowDescription:
										"Maximum grace period after an event allowed at this site. Caps each event type's value.",
									name: 'allowMinutesPost',
									type: 'TextField',
									htmlType: 'number',
								},
							]
						: []),
				],
			},
		],
	};

	return (
		<div>
			{data.emergencyMode && (
				<Alert sx={{ marginBottom: 4 }} variant="filled" severity="error">
					Site is currently in emergency mode, site access for all roles is switched to the emergency configuration.
				</Alert>
			)}
			<Form schema={form} onChange={onChange} originalValues={data} />
		</div>
	);
}

function SiteDetailPage() {
	const { id } = Route.useParams();
	const { site: initialSite } = Route.useLoaderData();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const { data: siteData, refetch: refetchSite } = useQuery({
		queryKey: ['system-site', id],
		queryFn: () => getSite(id),
		initialData: initialSite,
	});

	const [activeTab, setActiveTab] = useState(0);
	const [site, setSite] = useState<SiteFormValues>({
		...siteData,
		gracePeriodCapEnabled: siteData.allowMinutesPre != null || siteData.allowMinutesPost != null,
	});
	const [hasChanged, setChanged] = useState(false);

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Sites', '/system/sites');
	useBreadcrumbLevel(2, site?.name || 'Site Details');

	const isValid = true;

	const handleChange = (data: SiteFormValues) => {
		setChanged(true);
		setSite(data);
	};

	const saveChanges = async (data: SiteFormValues) => {
		try {
			const { gracePeriodCapEnabled, ...rest } = data;
			// Send explicit null to clear the cap when disabled; the API treats null/absent as "no cap".
			const payload = gracePeriodCapEnabled ? rest : { ...rest, allowMinutesPre: null, allowMinutesPost: null };
			await updateSite(siteData.id, payload as Partial<Site>);
			setChanged(false);
			EventEmitter.emit('toast', { type: 'success', message: 'Site updated!' });
			refetchSite();
			queryClient.invalidateQueries({ queryKey: ['system-sites'] });
		} catch (_err) {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to update site.' });
		}
	};

	const tabs = [
		{
			label: 'General',
			element: <GeneralTab key="0" data={site} onChange={handleChange} />,
		},
	];

	return (
		<div className="min-h-full w-full p-4">
			<Paper variant="outlined">
				<Tabs
					value={activeTab}
					onChange={(value) => setActiveTab(value)}
					actions={
						<Button
							disabled={!hasChanged || !isValid}
							size="small"
							variant="outlined"
							onClick={() => saveChanges(site)}
						>
							Save Changes
						</Button>
					}
				>
					<Tabs.TabList>
						{tabs.map((tab) => (
							<Tabs.Tab key={tab.label} value={0} label={tab.label} />
						))}
					</Tabs.TabList>
					{tabs.map((tab, index) => (
						<Tabs.TabPanel key={tab.label} value={index} selectedValue={activeTab}>
							<div>{tab.element}</div>
						</Tabs.TabPanel>
					))}
				</Tabs>
			</Paper>
		</div>
	);
}
